import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const DeliveriesPanel = ({ colors, rows = [], isLoading = false }) => {
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [releaseFilter, setReleaseFilter] = useState('');

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const productOptions = useMemo(() => {
    return [...new Set(
      safeRows
        .map((row) => row.product)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
  }, [safeRows]);

  const filteredRows = useMemo(() => {
    const orgNeedle = orgSearch.trim().toLowerCase();

    return safeRows.filter((row) => {
      const orgName = String(row.org_name || '').toLowerCase();
      const orgId = String(row.organization_id || '').toLowerCase();

      const matchesOrg =
        !orgNeedle ||
        orgName.includes(orgNeedle) ||
        orgId.includes(orgNeedle);

      const matchesProduct =
        !selectedProducts.length ||
        selectedProducts.includes(row.product);

      const matchesRelease =
        !releaseFilter ||
        row.release_type === releaseFilter;

      return matchesOrg && matchesProduct && matchesRelease;
    });
  }, [safeRows, orgSearch, selectedProducts, releaseFilter]);

  const hasFilters = Boolean(
    orgSearch.trim() ||
    selectedProducts.length ||
    releaseFilter
  );

  const clearFilters = () => {
    setOrgSearch('');
    setSelectedProducts([]);
    setReleaseFilter('');
  };

  const columns = useMemo(
    () => [
      {
        field: 'release_type',
        headerName: 'Track',
        width: 110,
        minWidth: 100,
        resizable: true,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value || '-'}
            sx={{
              bgcolor:
                value === 'Contact'
                  ? colors.greenAccent[800]
                  : value === 'Digital'
                    ? colors.blueAccent[800]
                    : colors.primary[300],
              color: colors.grey[100],
            }}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        minWidth: 110,
        resizable: true,
        renderCell: ({ value }) => {
          let bg = colors.primary[300];
          if (value === 'queued') bg = colors.blueAccent[700];
          if (value === 'delivering') bg = colors.blueAccent[500];
          if (value === 'delivered') bg = colors.greenAccent[700];
          if (value === 'failed') bg = colors.redAccent[700];
          if (value === 'skipped') bg = colors.grey[600];

          return (
            <Chip
              size="small"
              label={value || '-'}
              sx={{
                bgcolor: bg,
                color: colors.grey[100],
              }}
            />
          );
        },
      },
      {
        field: 'org_name',
        headerName: 'Org',
        width: 220,
        minWidth: 180,
        resizable: true,
      },
      {
        field: 'organization_id',
        headerName: 'Org ID',
        width: 120,
        minWidth: 100,
        resizable: true,
      },
      {
        field: 'product',
        headerName: 'Product',
        width: 200,
        minWidth: 180,
        resizable: true,
      },
      {
        field: 'version',
        headerName: 'Version',
        width: 120,
        minWidth: 100,
        resizable: true,
      },
      {
        field: 'delivery_target_type',
        headerName: 'Target',
        width: 120,
        minWidth: 100,
        resizable: true,
      },
      {
        field: 'file_format',
        headerName: 'Format',
        width: 110,
        minWidth: 95,
        resizable: true,
      },
      {
        field: 'full_s3_uri',
        headerName: 'S3 Destination',
        width: 330,
        minWidth: 240,
        resizable: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'snowflake_target',
        headerName: 'Snowflake Target',
        width: 300,
        minWidth: 220,
        resizable: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'delivery_run_id',
        headerName: 'Run ID',
        width: 220,
        minWidth: 180,
        resizable: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'delivery_date_utc',
        headerName: 'Scheduled/Delivery Date',
        width: 210,
        minWidth: 180,
        resizable: true,
        valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        field: 'date_last_checked_utc',
        headerName: 'Last Checked',
        width: 190,
        minWidth: 170,
        resizable: true,
        valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        field: 'total_files',
        headerName: 'Files',
        width: 90,
        minWidth: 80,
        resizable: true,
      },
      {
        field: 'total_bytes',
        headerName: 'Bytes',
        width: 110,
        minWidth: 90,
        resizable: true,
      },
      {
        field: 'last_error',
        headerName: 'Last Error',
        width: 320,
        minWidth: 240,
        resizable: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: value ? colors.redAccent[300] : colors.grey[300],
              }}
            >
              {value || ''}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [colors]
  );

  return (
    <>
      <Typography variant="caption" sx={{ color: colors.grey[300] }}>
        This view shows all release-linked current_delivery rows created for the finalized tracks of this release.
      </Typography>

      <Box
        mt={2}
        p={1.5}
        sx={{
          borderRadius: 1,
          backgroundColor: colors.primary[500],
          border: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            size="small"
            label="Search organization"
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            placeholder="Name or org ID"
            sx={{ minWidth: 240 }}
          />

          <Autocomplete
            multiple
            size="small"
            options={productOptions}
            value={selectedProducts}
            onChange={(e, value) => setSelectedProducts(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Products"
                placeholder="Filter products"
              />
            )}
            sx={{ minWidth: 300, flex: 1 }}
          />

          <TextField
            select
            size="small"
            label="Sub-release"
            value={releaseFilter}
            onChange={(e) => setReleaseFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Contact">Contact</MenuItem>
            <MenuItem value="Digital">Digital</MenuItem>
          </TextField>

          <Button
            variant="outlined"
            disabled={!hasFilters}
            onClick={clearFilters}
            sx={{
              minWidth: 110,
              borderColor: colors.blueAccent[300],
              color: colors.grey[100],
              '&:hover': {
                borderColor: colors.greenAccent[300],
                backgroundColor: colors.primary[400],
              },
            }}
          >
            Clear
          </Button>
        </Stack>

        <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mt: 1 }}>
          Showing {filteredRows.length} of {safeRows.length} delivery rows.
        </Typography>
      </Box>

      <Box mt={2} sx={{ height: 560 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          getRowHeight={() => 44}
          getRowId={(row) =>
            row.id ||
            [
              row.organization_mongo_id,
              row.organization_id,
              row.release_type,
              row.delivery_run_id,
              row.product,
              row.version,
              row.delivery_target_type,
              row.file_format,
              row.full_s3_uri,
              row.snowflake_target,
            ].filter(Boolean).join(':')
          }
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          sx={{
            border: `1px solid ${colors.primary[300]}`,
            backgroundColor: colors.primary[500],

            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent[700],
              borderBottom: `1px solid ${colors.primary[300]}`,
              overflow: 'visible',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${colors.primary[400]}`,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${colors.primary[300]}`,
              backgroundColor: colors.primary[400],
            },

            '& .MuiDataGrid-columnSeparator': {
              opacity: '1 !important',
              visibility: 'visible !important',
              color: colors.grey[300],
              minWidth: '10px',
              width: '10px',
              cursor: 'col-resize',
            },
            '& .MuiDataGrid-columnSeparator:hover': {
              color: colors.greenAccent[300],
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'visible',
            },
          }}
        />
      </Box>
    </>
  );
};

export default DeliveriesPanel;